import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector } from "react-redux";
import { WizardDialog, WizardStep } from "component/shared/wizard-dialog";
import { DialogLink } from "component/shared/dialog-frame";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { Switch } from "component/shared/switch";
import { Tag } from "component/shared/tag";
import { BrandCheckbox } from "component/shared/brand-checkbox";
import { RootState, useAppThunkDispatch } from "redux/store";
import { getUserUnsafe } from "redux/selector";
import { saveUser } from "redux/slice/authentication";
import { setupPayload } from "../payload";
import { UserPartial } from "model/user";
import { TimeZoneType } from "type/time-zone";
import { formatPhoneInput, isValidPhone, normalizeTaxId } from "shared/texting-fields";
import { AreaCode } from "../area-code";
import { TaxIdFields, taxIdLabel } from "../tax-id-fields";
import { answerTaxId, initialSetupForm, setupStepKeys, SetupForm, SetupStepKey, stepErrors } from "./steps";
import { TermsDialog } from "./terms-dialog";
import { FieldHelper } from "./field-helper";
import { styles } from "./style";

interface OwnProps {
	open: boolean;
	onClose: (completed: boolean) => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

const Component: React.FC<Props> = ({ classes = {}, open, onClose }) => {
	const dispatch = useAppThunkDispatch();
	const user = useSelector(getUserUnsafe);
	const loading = useSelector((state: RootState) => state.authentication.loading);
	const error = useSelector((state: RootState) => state.authentication.error);
	const defaultTaxIdType = user?.textMessageTaxIdDefaultType ?? "EIN";

	const [form, setForm] = useState<SetupForm>(() => initialSetupForm(defaultTaxIdType));
	const [areaCodeReady, setAreaCodeReady] = useState(false);
	const [areaCodeShown, setAreaCodeShown] = useState(false);
	const [shown, setShown] = useState<Partial<Record<SetupStepKey, boolean>>>({});
	const [termsOpen, setTermsOpen] = useState(false);

	useEffect(() => {
		if (open) {
			setForm(initialSetupForm(defaultTaxIdType));
			setAreaCodeReady(user?.textMessageAreaCode?.length === 3);
			setAreaCodeShown(false);
			setShown({});
		}
	}, [open]); // eslint-disable-line

	const update = (patch: Partial<SetupForm>) => setForm((current) => ({ ...current, ...patch }));
	const errorsFor = (key: SetupStepKey) => (shown[key] ? stepErrors(key, form) : {});
	// Continue stays enabled so a click can show what's missing, as the Figma error states do.
	const guard = (key: SetupStepKey) => () => {
		if (Object.keys(stepErrors(key, form)).length) {
			setShown((current) => ({ ...current, [key]: true }));
			return false;
		}
	};

	const withError = (field: React.ReactNode, error?: string) => (
		<div className={classes.field}>
			<div className={classes.input}>{field}</div>
			{error && <FieldHelper variant="error">{error}</FieldHelper>}
		</div>
	);

	const phoneField = (label: string, field: "mobile" | "callForwarding", error?: string) => withError(
		<FormField
			id={`texting-setup-${field}`}
			label={label}
			required
			value={form[field]}
			onChange={(event) => update({ [field]: formatPhoneInput(event.target.value) })}
			error={!!error}
		/>,
		error,
	);

	const taxIdErrors = errorsFor("tax-id");
	const mobileErrors = errorsFor("mobile-verification");
	const settingsErrors = errorsFor("additional-settings");
	const termsErrors = errorsFor("terms");

	const stepsByKey: Record<SetupStepKey, WizardStep> = {
		"area-code": {
			key: "area-code",
			title: "Select Area Code",
			description: (
				<Mui.Typography className={classes.text}>
					Your subscribers will receive text messages from a number with this area code. We'll assign a number automatically.
				</Mui.Typography>
			),
			onContinue: () => {
				if (!areaCodeReady || loading || error) {
					setAreaCodeShown(true);
					return false;
				}
			},
			content: <AreaCode showRequired={areaCodeShown && !areaCodeReady} onSave={(areaCode) => setAreaCodeReady(areaCode?.length === 3)} />,
		},
		"tax-id": {
			key: "tax-id",
			title: "Add Business Tax ID",
			description: (
				<Mui.Typography className={classes.text}>
					If you're not sure whether your business has one, check your W-9 or IRS CP 575 letter, or ask your broker or accountant.
				</Mui.Typography>
			),
			onContinue: guard("tax-id"),
			content: (
				<TaxIdFields
					described
					hasTaxId={form.hasTaxId}
					onHasTaxIdChange={(hasTaxId) => setForm((current) => answerTaxId(current, hasTaxId, defaultTaxIdType))}
					complianceNote="Your EIN and legal business name must match your official registration for messaging compliance."
					taxIdType={form.taxIdType}
					onTaxIdTypeChange={(taxIdType) => update({ taxIdType })}
					taxIdField={withError(
						<FormField
							id="texting-setup-tax-id"
							label={taxIdLabel(form.taxIdType)}
							required
							value={form.taxId}
							onChange={(event) => update({ taxId: event.target.value })}
							error={!!taxIdErrors.taxId}
						/>,
						taxIdErrors.taxId,
					)}
					businessType={form.businessType}
					onBusinessTypeChange={(businessType) => update({ businessType })}
					businessTypeError={taxIdErrors.businessType}
					businessTypeHelper={taxIdErrors.businessType && <FieldHelper variant="error">{taxIdErrors.businessType}</FieldHelper>}
				>
					{withError(
						<FormField
							id="texting-setup-legal-name"
							label="Legal Business Name"
							required
							value={form.legalBusinessName}
							onChange={(event) => update({ legalBusinessName: event.target.value })}
							error={!!taxIdErrors.legalBusinessName}
						/>,
						taxIdErrors.legalBusinessName,
					)}
					{phoneField("Mobile Phone Number", "mobile", taxIdErrors.mobile)}
				</TaxIdFields>
			),
		},
		"mobile-verification": {
			key: "mobile-verification",
			title: "Mobile Verification",
			description: (
				<>
					<Mui.Typography className={classes.text}>
						The Campaign Registry (TCR) requires text message verification for your mobile number. You must respond "YES" to the text from TCR within 24 hours.
					</Mui.Typography>
					<Mui.Typography className={classes.text}>
						This number can't be a Google Voice or VoIP number.
					</Mui.Typography>
				</>
			),
			onContinue: guard("mobile-verification"),
			content: phoneField("Mobile Phone Number", "mobile", mobileErrors.mobile),
		},
		"additional-settings": {
			key: "additional-settings",
			title: "Additional Settings",
			onContinue: guard("additional-settings"),
			content: (
				<>
					<div className={classes.section}>
						<Mui.Typography component="h4" className={classes.sectionTitle}>Call Forwarding</Mui.Typography>
						<Mui.Typography className={classes.text}>
							This number will be used if a lead calls your texting number. Google Voice and VoIP numbers are allowed.
						</Mui.Typography>
					</div>
					{phoneField("Call Forwarding Phone Number", "callForwarding", settingsErrors.callForwarding)}
					<Mui.Divider />
					<div className={classes.section}>
						<Mui.Typography component="h4" className={classes.sectionTitle}>Time Zone</Mui.Typography>
						<Mui.Typography className={classes.text}>
							We use this to schedule your texts within allowed sending hours.
						</Mui.Typography>
					</div>
					{withError(
						<FormSelectField
							label="Time Zone"
							required
							value={form.timeZone}
							onChange={(timeZone) => update({ timeZone })}
							options={TimeZoneType.values()}
							valueExtractor={(option) => option.id}
							labelExtractor={(option) => option.label}
							error={!!settingsErrors.timeZone}
						/>,
						settingsErrors.timeZone,
					)}
					<Mui.Divider />
					<Mui.Typography component="h4" className={classes.sectionTitle}>Lead capture</Mui.Typography>
					<Switch
						checked={form.phoneRequired}
						onChange={(phoneRequired) => update({ phoneRequired })}
						label={
							<span className={classes.switchLabel}>
								Require phone number on lead capture forms
								<Tag label="Recommended" variant="success" size="small" />
							</span>
						}
						description="Leads without a phone number can't receive texts. Requiring it here ensures they can opt in to text messages."
					/>
				</>
			),
		},
		terms: {
			key: "terms",
			title: "Terms of Service",
			description: (
				<>
					<Mui.Typography className={classes.text}>
						By enabling text messaging, you agree to iHomefinder's{" "}
						<DialogLink
							href="#"
							onClick={(event: React.MouseEvent) => {
								event.preventDefault();
								setTermsOpen(true);
							}}
						>
							Texting Terms of Service
						</DialogLink>{" "}
						which includes:
					</Mui.Typography>
					<ul className={classes.terms}>
						<li>
							Client acknowledges that Client is solely responsible for all acts or omissions or violations of law that may occur in connection with Client's use of the Licensed Technology.
						</li>
						<li>
							Client represents and warrants that the owners of the phone number to which Client transmits any outbound text messages through the Licensed Technology have expressly consented or otherwise opted-in to the receipt of such messages, in accordance with the TCPA, the TSR, and other applicable federal, state, local laws, statutes and regulations and industry guidelines.
						</li>
						<li>
							Client agrees that Client will not access or otherwise use any third-party list of phone numbers or otherwise engage in unsolicited messaging in connection with the Licensed Technology.
						</li>
					</ul>
					{form.phoneRequired && (
						<Mui.Typography className={classes.text}>
							Going forward, lead capture forms will require a phone number so leads can opt in to texting.
						</Mui.Typography>
					)}
				</>
			),
			onContinue: guard("terms"),
			content: (
				<div className={classes.agree}>
					<Mui.FormControlLabel
						className={classes.agreeLabel}
						control={
							<BrandCheckbox
								className={classes.checkbox}
								checked={form.agreeToTerms}
								onChange={(event) => update({ agreeToTerms: event.target.checked })}
							/>
						}
						label={<Mui.Typography className={classes.text}>I have read and agree to the Texting Terms of Service</Mui.Typography>}
					/>
					{termsErrors.agreeToTerms && (
						<FieldHelper variant="error" className={classes.agreeError}>{termsErrors.agreeToTerms}</FieldHelper>
					)}
				</div>
			),
		},
	};

	const steps = setupStepKeys(form.hasTaxId).map((key) => stepsByKey[key]);

	const handleComplete = async () => {
		if (!user) {
			return;
		}
		const update = setupPayload(user, form);
		await dispatch(saveUser({ user: update })).unwrap();
		onClose(true);
	};

	return (
		<>
			<WizardDialog
				open={open}
				title="Texting Setup"
				steps={steps}
				onClose={() => onClose(false)}
				onComplete={handleComplete}
				completeLabel="Finish"
				cancelLabel="Cancel"
				errorMessage="We couldn't finish your texting setup. Please try again."
			/>
			<TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
		</>
	);
};

export const SetupDialog = withStyles(Component, styles);
